import { BASE_URL } from '../BASE_CONSTS';

const normalizeBase = (base) => String(base || '').replace(/\/+$/, '');

const firstMedia = (media) => {
  if (Array.isArray(media)) return media[0] || null;
  return media || null;
};

const mediaPath = (media, preferredFormat = 'medium') => {
  const item = firstMedia(media);
  if (!item) return null;
  if (typeof item === 'string') return item;

  const formats = item.formats || {};
  return (
    formats[preferredFormat]?.url ||
    formats.small?.url ||
    formats.thumbnail?.url ||
    formats.medium?.url ||
    item.url ||
    null
  );
};

export const getPlayerPhotoUrl = (record, baseUrl = BASE_URL, preferredFormat = 'medium') => {
  const path = mediaPath(record?.avatar, preferredFormat);
  if (!path) return null;
  if (/^https?:\/\//i.test(path) || /^data:/i.test(path) || /^blob:/i.test(path)) return path;
  return `${normalizeBase(baseUrl)}/${String(path).replace(/^\/+/, '')}`;
};

const asPositiveNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

const maxField = (statistics, field, validator = () => true) => {
  const values = (Array.isArray(statistics) ? statistics : [])
    .map((row) => asPositiveNumber(row?.[field]))
    .filter((value) => value !== null && validator(value));
  return values.length ? Math.max(...values) : null;
};

export const getPlayerBestMetrics = (record = {}) => {
  const statistics = record.Statistics || [];
  return {
    speed: maxField(statistics, 'Speed_s_razbega'),
    hit: maxField(statistics, 'Hit'),
    control: maxField(statistics, 'FootSkill2'),
    dribbling: maxField(statistics, 'ExstraColorDriblingTest', (value) => value <= 5),
    agility: maxField(statistics, 'Agility'),
  };
};

export const getBirthYear = (birthday) => {
  const match = String(birthday || '').match(/^(\d{4})/);
  return match ? Number(match[1]) : null;
};

const average = (values) => {
  const clean = values.filter((value) => Number.isFinite(value));
  if (!clean.length) return null;
  return clean.reduce((sum, value) => sum + value, 0) / clean.length;
};

export const buildDashboardPlayers = (players = []) => {
  const prepared = (Array.isArray(players) ? players : []).map((player) => ({
    ...player,
    bestMetrics: getPlayerBestMetrics(player),
    birthYear: getBirthYear(player?.birthday),
  }));

  return prepared.map((player) => {
    const metricKeys = Object.keys(player.bestMetrics);
    if (!player.birthYear) {
      const cohortAverages = metricKeys.reduce((result, key) => ({ ...result, [key]: null }), {});
      return { ...player, cohortAverages };
    }

    const cohort = prepared.filter((candidate) => candidate.birthYear === player.birthYear);
    const cohortAverages = metricKeys.reduce((result, key) => {
      result[key] = average(cohort.map((candidate) => candidate.bestMetrics[key]));
      return result;
    }, {});
    return { ...player, cohortAverages };
  });
};

export const formatMetricValue = (key, value) => {
  if (!Number.isFinite(value)) return '—';
  if (key === 'agility') return Number(value).toFixed(1);
  return String(Math.round(value));
};

export const PLAYER_CARD_METRICS = [
  { key: 'speed', label: 'СКР' },
  { key: 'hit', label: 'УДР' },
  { key: 'control', label: 'КНТ' },
  { key: 'dribbling', label: 'ДРБ' },
  { key: 'agility', label: 'ЛОВ' },
];
